import type { IntlShape } from 'react-intl'
import type { UiNode, UiNodeGroupEnum, UiNodeInputAttributes, UiText } from '@ory/client'

import { translateKratosText } from '@i18n/kratos/kratos-i18n'

// Group filter that always pulls the `default` group alongside the requested
// one — csrf_token / submit method nodes live there and every panel needs
// them. Centralised so adding the TOTP group later is a one-line change.
export const nodesForGroup = (nodes: ReadonlyArray<UiNode>, group: UiNodeGroupEnum): UiNode[] =>
  nodes.filter((n) => n.group === group || n.group === 'default')

// `UiNode.attributes` is a discriminated union (anchor / image / input / script
// / text). The discriminator is a numeric enum that doesn't narrow cleanly with
// `is`-predicates, so we treat input attributes as the default shape and access
// `.name` defensively.
export const inputAttrs = (n: UiNode): UiNodeInputAttributes | null => {
  const a = n.attributes as Partial<UiNodeInputAttributes>
  return typeof a.name === 'string' ? (n.attributes as UiNodeInputAttributes) : null
}

export const findInput = (
  nodes: ReadonlyArray<UiNode>,
  name: string,
  type?: string,
): UiNodeInputAttributes | null => {
  for (const n of nodes) {
    const a = inputAttrs(n)
    if (!a || a.name !== name) {
      continue
    }
    if (type && a.type !== type) {
      continue
    }
    return a
  }
  return null
}

export const findFieldError = (
  nodes: ReadonlyArray<UiNode>,
  name: string,
  intl: IntlShape,
): string | undefined => {
  const node = nodes.find((n) => inputAttrs(n)?.name === name)
  const errorMsg = node?.messages?.find((m: UiText) => m.type === 'error')
  return errorMsg ? translateKratosText(errorMsg, intl) : undefined
}

// Guards a client-chosen provider id (remembered / HRD-resolved) against a
// stale or misconfigured entry Kratos doesn't actually offer on this flow.
export const hasProviderNode = (nodes: ReadonlyArray<UiNode>, providerId: string): boolean =>
  nodes.some((n) => {
    const a = inputAttrs(n)
    return a?.name === 'provider' && String(a.value) === providerId
  })

// Submit node Kratos emits for the chosen flow method (e.g. recovery `code` or
// `link`). Derive instead of hardcoding so config flips don't silently break.
export const findSubmitMethod = (nodes: ReadonlyArray<UiNode>): UiNodeInputAttributes | null => {
  for (const n of nodes) {
    const a = inputAttrs(n)
    if (a?.name === 'method' && a.type === 'submit') {
      return a
    }
  }
  return null
}

// IDs Kratos stamps on the *new* flow it creates after the previous one
// expired or was otherwise restarted (login 4010001, registration 4040001,
// recovery 4060005, settings 4070005). The new flow is valid and submittable
// — Kratos emits these as `type: 'info'`, not errors. Surfacing them on a
// fresh form just confuses the user ("flow restarted? I only just opened
// the page") without conveying anything actionable.
const FLOW_RESTARTED_MESSAGE_IDS: ReadonlySet<number> = new Set([
  4010001, 4040001, 4060005, 4070005,
])

// Error + info only. Success messages have specific ids (Kratos 1050xxx) and
// the caller renders them distinctly (e.g. dedicated Alert) — including them
// here would render the same message twice on a successful settings update.
export const getFlowMessages = (flow: { ui: { messages?: UiText[] } }): UiText[] =>
  (flow.ui.messages || []).filter(
    (m) => (m.type === 'error' || m.type === 'info') && !FLOW_RESTARTED_MESSAGE_IDS.has(m.id),
  )
